import type { ActionFunctionArgs } from "react-router";
import { Form } from "react-router";

import { ClientOnly, Flex, Heading, Image } from "@vygruppen/spor-react";

import { authenticateWithCognito } from "~/features/auth/auth.server";
import { returnToCookie } from "~/features/auth/return-to-cookie.server";
import { commonLogger } from "~/features/logging/logging.server";
import { useSeasonalIllustration } from "~/features/seasonal-decor/seasonal-illustrations/use-seasonal-illustration";
import { NetworkDependentButton } from "~/features/ui/NetworkDependentButton";

export async function action({ request }: ActionFunctionArgs) {
  const returnTo = new URL(request.url).searchParams.get("returnTo");

  try {
    // This will throw a redirect to Cognito if no session exists
    // If session exists, it returns the session cookie
    const sessionCookie = await authenticateWithCognito(request);

    // If we get here, we have a valid session - shouldn't normally happen
    // on initial login since the OAuth flow requires a redirect
    return new Response(null, {
      headers: {
        "Set-Cookie": sessionCookie,
      },
    });
  } catch (error) {
    // OAuth redirect - add returnTo cookie and re-throw
    // Using reloadDocument on the form means this is a traditional form submission,
    // so the browser will handle the redirect normally
    if (isRedirect(error) && returnTo) {
      error.headers.append(
        "Set-Cookie",
        await returnToCookie.serialize(returnTo),
      );
    }

    if (!isRedirect(error)) {
      commonLogger.error("authentication_error", { error });
    }

    throw error;
  }
}

function isRedirect(error: unknown): error is Response {
  return (
    error instanceof Response &&
    error.status >= 300 &&
    error.status < 400 &&
    error.headers.has("Location")
  );
}

export default function Login() {
  const illustrationSlug = useSeasonalIllustration("konduktoer-dark", "dark");

  // TODO: Fallback to local auth somehow
  return (
    <Flex
      as="main"
      flexDirection="column"
      backgroundColor="jungle"
      padding={0}
      alignItems="center"
      justifyContent="center"
      flex={1}
      maxW="full"
      marginX={0}
      height="100dvh"
    >
      <Image
        src={`/illustrations/${illustrationSlug}.svg`}
        alt=""
        height="25vh"
      />
      <Heading as="h2" fontWeight="bold" variant="xl-display" color="white">
        Velkommen!
      </Heading>
      <Form method="post" reloadDocument>
        <ClientOnly>
          <NetworkDependentButton
            type="submit"
            variant="primary"
            size="lg"
            width="md"
            marginTop={2}
            maxWidth="95vw"
          >
            Logg inn
          </NetworkDependentButton>
        </ClientOnly>
      </Form>
    </Flex>
  );
}
